import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Crown, MapPin, User, CalendarClock } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { HOUSES } from "@/i18n/sections";

export function FamilyTree() {
  const { t } = useLanguage();
  const ft = t.familyTree;
  const [active, setActive] = useState("sleat");
  const house = HOUSES.find((h) => h.id === active);

  return (
    <section id="houses" data-testid="family-tree-section" className="relative bg-clan-bg py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <Overline>{ft.overline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
              {ft.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-clan-muted">{ft.subtitle}</p>
          </Reveal>
        </div>

        {/* Root node */}
        <Reveal delay={0.1}>
          <div className="mt-14 flex items-center gap-4 rounded-lg border border-clan-accent/30 bg-clan-accent/5 p-5">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md border border-clan-accent/50 text-clan-accent">
              <Crown className="h-5 w-5" />
            </span>
            <div>
              <p className="font-serif text-2xl text-clan-ink">{ft.rootName}</p>
              <p className="text-sm text-clan-muted">{ft.rootTagline}</p>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-12">
          {/* House selector rail */}
          <div className="lg:col-span-5">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-clan-muted">{ft.labels.explore}</p>
            <div className="flex flex-col gap-2.5">
              {HOUSES.map((h, i) => {
                const isActive = h.id === active;
                return (
                  <motion.button
                    key={h.id}
                    data-testid={`house-card-${h.id}`}
                    onClick={() => setActive(h.id)}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className={`group flex items-center justify-between rounded-md border px-5 py-4 text-left transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-clan-accent ${
                      isActive
                        ? "border-clan-accent/60 bg-clan-surface"
                        : "border-clan-ink/10 bg-transparent hover:border-clan-ink/20 hover:bg-clan-ink/[0.04]"
                    }`}
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className={`grid h-9 w-9 shrink-0 place-items-center rounded-md border font-serif text-sm transition-colors duration-300 ${
                          isActive ? "border-clan-accent bg-clan-accent text-black" : "border-clan-ink/15 text-clan-muted"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span>
                        <span className={`block font-serif text-lg ${isActive ? "text-clan-ink" : "text-clan-ink/80"}`}>
                          {h.name}
                        </span>
                        <span className="block font-mono text-[11px] uppercase tracking-widest text-clan-muted">{h.era}</span>
                      </span>
                    </span>
                    <ChevronRight
                      className={`h-4 w-4 shrink-0 transition-all duration-300 ${
                        isActive ? "translate-x-0 text-clan-accent" : "-translate-x-1 text-clan-muted group-hover:translate-x-0"
                      }`}
                    />
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-7">
            <div className="h-full overflow-hidden rounded-lg border border-clan-ink/10 bg-clan-surface">
              <div className="tartan-weave h-2 w-full" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  data-testid="house-detail"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="p-8 lg:p-12"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-clan-accent">{ft.overline}</p>
                  <h3 className="mt-3 font-serif text-3xl font-medium text-clan-ink lg:text-4xl">{house.name}</h3>
                  <p className="mt-5 text-base leading-relaxed text-clan-muted">{ft.houses[house.id]}</p>

                  <div className="mt-8 grid gap-5 border-t border-clan-ink/10 pt-8 sm:grid-cols-3">
                    <Detail icon={<MapPin className="h-4 w-4" />} label={ft.labels.seat} value={house.seat} />
                    <Detail icon={<User className="h-4 w-4" />} label={ft.labels.founder} value={house.founder} />
                    <Detail icon={<CalendarClock className="h-4 w-4" />} label={ft.labels.era} value={house.era} />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div>
      <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-clan-accent">
        {icon}
        {label}
      </span>
      <p className="mt-2 text-sm text-clan-ink">{value}</p>
    </div>
  );
}
